import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { StyleSheet, View, ActivityIndicator, TextInput, Platform, Keyboard, Pressable } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useFocusEffect } from "@react-navigation/native";
import Animated from "react-native-reanimated";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { Button } from "@/components/ui/Button";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { twilioSmsService, SMSMessage } from "@/services/twilioSmsService";
import { useSMSPhone } from "../_layout";
import { useSMSRealtime } from "@/hooks/useSMSRealtime";
import { useSMS } from "@/hooks/useSMS";
import { useCall } from "@/hooks/useCall";
import { callService, CallHistoryRecord } from "@/services/callService";
import { format } from "date-fns";
import { formatPhoneNumber, normalizePhoneNumber } from "@/lib/phone";
import { supabase } from "@/lib/supabase-client";
import Avatar from "@/components/Avatar";
import useAuth from "@/hooks/useAuth";
import { userService } from "@/services/userService";

type TimelineItem =
  | { type: "message"; data: SMSMessage; date: string }
  | { type: "call"; data: CallHistoryRecord; date: string };

type MatchedUser = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
};

export default function SMSConversationDetailPage() {
  const { conversation } = useLocalSearchParams<{ conversation: string }>();
  const router = useRouter();
  const { phoneNumber: twilioNumber } = useSMSPhone();
  const { colors } = useTheme();
  const { markConversationRead } = useSMS();
  const call = useCall();
  const { user } = useAuth();
  const [userSignature, setUserSignature] = useState("");

  // Fetch current user's profile for signature
  useEffect(() => {
    if (!user?.id) return;

    userService.getUserById(user.id).then((profile) => {
      if (profile) {
        setUserSignature(profile.preferred_name || profile.first_name || "");
      }
    });
  }, [user?.id]);

  // Canonicalize to E.164 so the conversation key matches what we persist (Twilio's
  // E.164). Without this, a formatted route param like "(405) 821-0142" never matches
  // the DB rows / realtime broadcasts (which use "+14058210142").
  const externalNumber = normalizePhoneNumber(decodeURIComponent(conversation || ""));
  const formattedExternalNumber = formatPhoneNumber(externalNumber);
  // Normalize to 10 digits (strip +1 or leading 1 country code)
  const normalizedExternalNumber = (() => {
    const digits = externalNumber.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      return digits.slice(1);
    }
    return digits;
  })();

  const [messages, setMessages] = useState<SMSMessage[]>([]);
  const [callHistory, setCallHistory] = useState<CallHistoryRecord[]>([]);
  const [callUsers, setCallUsers] = useState<Record<string, MatchedUser>>({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [matchedUser, setMatchedUser] = useState<MatchedUser | null>(null);
  const [nameHovered, setNameHovered] = useState(false);

  const listRef = useRef<Animated.FlatList<TimelineItem>>(null);
  const inputRef = useRef<TextInput>(null);

  // Merge messages and calls into a single timeline sorted by date
  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      ...messages.map(
        (m): TimelineItem => ({
          type: "message",
          data: m,
          date: m.dateSent || m.dateCreated,
        }),
      ),
      ...callHistory.map(
        (c): TimelineItem => ({
          type: "call",
          data: c,
          date: c.created_at,
        }),
      ),
    ];
    // Newest first: the list is `inverted`, so index 0 renders at the bottom. This keeps
    // the view pinned to the latest message on load, send, and inbound — no scroll hacks.
    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [messages, callHistory]);

  // Resolve user_ids from call history records for avatar display
  useEffect(() => {
    const ids = [...new Set(callHistory.map((c) => c.user_id).filter(Boolean))] as string[];
    // Only fetch IDs we don't already have
    const missing = ids.filter((id) => !callUsers[id]);
    if (missing.length === 0) return;

    supabase
      .from("users")
      .select("id, first_name, last_name, preferred_name")
      .in("id", missing)
      .then(({ data }) => {
        if (!data) return;
        setCallUsers((prev) => {
          const next = { ...prev };
          for (const u of data) {
            next[u.id] = {
              id: u.id,
              first_name: u.first_name,
              last_name: u.last_name,
              preferred_name: u.preferred_name,
            };
          }
          return next;
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callHistory]);

  // Handle incoming SMS via realtime
  const handleNewMessage = useCallback(
    (message: SMSMessage) => {
      // Only add message if it's from the current conversation
      if (message.from === externalNumber || message.to === externalNumber) {
        setMessages((prev) => {
          // Already have it (by sid) — ignore the echo.
          if (prev.some((m) => m.sid === message.sid)) {
            return prev;
          }
          // Our own outbound now echoes back via postgres_changes; drop the optimistic
          // temp entry (matched by body + direction) so it doesn't double up.
          const deduped = prev.filter(
            (m) => !(m.sid.startsWith("temp-") && m.direction === message.direction && m.body === message.body),
          );
          return [...deduped, message];
        });
        // The inverted list stays pinned to newest, so no manual scroll is needed.
      }
    },
    [externalNumber],
  );

  // Subscribe to realtime SMS updates
  useSMSRealtime({
    phoneNumber: twilioNumber,
    onNewMessage: handleNewMessage,
    enabled: !!twilioNumber,
  });

  const loadMessages = useCallback(async () => {
    if (!twilioNumber || !externalNumber) return;

    try {
      setLoading(true);
      setError(null);
      const [smsData, callData] = await Promise.all([
        twilioSmsService.getMessages(twilioNumber, externalNumber),
        callService.getCallsBetween(twilioNumber, externalNumber).catch(() => []),
      ]);
      setMessages(smsData);
      setCallHistory(callData);
    } catch (err: any) {
      console.error("Failed to fetch SMS messages:", err);
      setError(err.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [twilioNumber, externalNumber]);

  useFocusEffect(
    useCallback(() => {
      loadMessages();
    }, [loadMessages]),
  );

  // Refetch call history when call state changes (call started or ended)
  const { callState } = call;
  const prevCallState = useRef(callState);
  useEffect(() => {
    if (prevCallState.current !== callState) {
      prevCallState.current = callState;
      if (callState === "outgoing" || callState === "active" || callState === "idle") {
        // Small delay to let the DB write settle
        const timer = setTimeout(() => {
          callService
            .getCallsBetween(twilioNumber, externalNumber)
            .then(setCallHistory)
            .catch(() => {});
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [callState, twilioNumber, externalNumber]);

  // Mark conversation as read when entering and when new messages arrive
  useEffect(() => {
    if (twilioNumber && externalNumber && messages.length > 0) {
      markConversationRead(twilioNumber, externalNumber);
    }
  }, [twilioNumber, externalNumber, messages.length, markConversationRead]);

  // Look up user by cell phone number
  useEffect(() => {
    if (!normalizedExternalNumber) return;
    let cancelled = false;
    (async () => {
      try {
        const users = await userService.getUsersByCellPhones([normalizedExternalNumber]);
        if (cancelled) return;
        const found = users.find((u) => u.last10 === normalizedExternalNumber.slice(-10));
        if (found) {
          setMatchedUser({
            id: found.id,
            first_name: found.first_name,
            last_name: found.last_name,
            preferred_name: found.preferred_name,
          });
        }
      } catch (err) {
        console.error("Failed to lookup user:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [normalizedExternalNumber]);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || sending) return;

    // Append signature to message
    const messageWithSignature = userSignature ? `${text}\n- ${userSignature}` : text;

    try {
      setSending(true);
      await twilioSmsService.sendMessage(twilioNumber, externalNumber, messageWithSignature);
      setDraft("");

      // Optimistically add the message
      const optimisticMessage: SMSMessage = {
        sid: `temp-${Date.now()}`,
        body: messageWithSignature,
        from: twilioNumber,
        to: externalNumber,
        direction: "outbound",
        dateSent: null,
        dateCreated: new Date().toISOString(),
        status: "queued",
      };
      setMessages((prev) => [...prev, optimisticMessage]);
      // Auto-scroll handled by the list's onContentSizeChange (fires after layout).

      // Reload to get the actual message from Twilio
      setTimeout(() => loadMessages(), 2000);
    } catch (err: any) {
      console.error("Failed to send SMS:", err);
      setError(err.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (loading && messages.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <ThemedText style={styles.loadingText}>Loading messages...</ThemedText>
      </View>
    );
  }

  if (error && messages.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>{error}</ThemedText>
      </View>
    );
  }

  return (
    <GestureHandlerRootView style={styles.container}>
      <View style={[styles.header, { borderColor: colors.border }]}>
        <View style={styles.headerRow}>
          {matchedUser ? (
            <View style={styles.headerWithAvatar}>
              <Avatar userId={matchedUser.id} size={36} />
              <View style={styles.headerTextContainer}>
                <Pressable
                  style={{ alignSelf: "flex-start" }}
                  onPress={() => router.setParams({ panel: "user", id: matchedUser.id })}
                  onHoverIn={() => setNameHovered(true)}
                  onHoverOut={() => setNameHovered(false)}
                >
                  <ThemedText
                    style={[
                      styles.headerTitle,
                      {
                        color: colors.primary,
                        borderBottomWidth: StyleSheet.hairlineWidth,
                        borderBottomColor: nameHovered ? colors.primary : "transparent",
                      },
                    ]}
                  >
                    {matchedUser.preferred_name ||
                      `${matchedUser.first_name || ""} ${matchedUser.last_name || ""}`.trim()}
                  </ThemedText>
                </Pressable>
                <ThemedText style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
                  {formattedExternalNumber}
                </ThemedText>
              </View>
            </View>
          ) : (
            <ThemedText style={styles.headerTitle}>{formattedExternalNumber}</ThemedText>
          )}
          <Button
            icon="phone"
            iconSize={18}
            style={[styles.callButton, { borderColor: colors.border }]}
            onPress={() => {
              const displayName = matchedUser
                ? matchedUser.preferred_name || `${matchedUser.first_name || ""} ${matchedUser.last_name || ""}`.trim()
                : formattedExternalNumber;
              call.startCall({
                callerName: displayName,
                callerUserId: matchedUser?.id,
                phoneNumber: externalNumber,
                fromNumber: twilioNumber,
              });
            }}
          />
        </View>
      </View>

      <Animated.FlatList
        ref={listRef}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        data={timeline}
        inverted
        keyExtractor={(item) => (item.type === "message" ? item.data.sid : `call-${item.data.id}`)}
        renderItem={({ item }) => {
          if (item.type === "call") {
            const c = item.data;
            const isOutbound = c.direction === "outbound";
            const isMissed = c.status === "no-answer" || c.status === "busy" || c.status === "failed";
            const callUser = c.user_id ? callUsers[c.user_id] : null;
            const callUserName = callUser
              ? callUser.preferred_name || `${callUser.first_name || ""} ${callUser.last_name || ""}`.trim()
              : null;

            const statusLabel =
              c.status === "completed"
                ? isOutbound
                  ? "Outbound call"
                  : "Incoming call"
                : c.status === "no-answer" || c.status === "busy"
                  ? "Missed call"
                  : c.status === "failed"
                    ? "Failed call"
                    : c.status === "canceled"
                      ? "Cancelled call"
                      : c.status === "ringing"
                        ? isOutbound
                          ? "Outbound · ringing"
                          : "Incoming · ringing"
                        : c.status === "initiated"
                          ? isOutbound
                            ? "Outbound · initiated"
                            : "Incoming · initiated"
                          : c.status === "in-progress"
                            ? "In progress"
                            : `${isOutbound ? "Outbound" : "Incoming"} · ${c.status}`;
            const durationText =
              c.duration && c.duration > 0
                ? c.duration >= 60
                  ? `${Math.floor(c.duration / 60)}m ${c.duration % 60}s`
                  : `${c.duration}s`
                : null;
            return (
              <View style={styles.callEvent}>
                <View style={[styles.callEventPill, { backgroundColor: colors.backgroundSecondary }]}>
                  {callUser && <Avatar userId={callUser.id} size={18} />}
                  <MaterialCommunityIcons
                    name={isMissed ? "phone-missed" : isOutbound ? "phone-outgoing" : "phone-incoming"}
                    size={14}
                    color={isMissed ? colors.red.text : colors.emerald.text}
                  />
                  <ThemedText style={[styles.callEventText, { color: colors.textSecondary }]}>
                    {callUserName && `${callUserName} · `}
                    {statusLabel}
                    {durationText && ` · ${durationText}`}
                  </ThemedText>
                </View>
                <ThemedText style={[styles.callEventTime, { color: colors.placeholder }]}>
                  {format(new Date(c.created_at), "MMM d, h:mm a")}
                </ThemedText>
              </View>
            );
          }

          const msg = item.data;
          const isOutbound = msg.direction === "outbound";
          const messageDate = msg.dateSent || msg.dateCreated;

          return (
            <View style={[styles.messageContainer, isOutbound && styles.messageContainerOutbound]}>
              <View
                style={[
                  styles.messageBubble,
                  { borderColor: colors.border, backgroundColor: colors.card },
                  isOutbound && { backgroundColor: colors.primary },
                ]}
              >
                <ThemedText style={[styles.messageText, isOutbound && { color: "#fff" }]}>{msg.body}</ThemedText>
              </View>
              <ThemedText style={[styles.timestamp, { color: colors.placeholder }]}>
                {format(new Date(messageDate), "MMM d, h:mm a")}
                {msg.status && msg.status !== "delivered" && msg.status !== "sent" && ` - ${msg.status}`}
              </ThemedText>
            </View>
          );
        }}
      />

      <View style={[styles.inputContainer, { borderColor: colors.border, backgroundColor: colors.card }]}>
        <View style={[styles.inputWrapper, { borderColor: colors.border, backgroundColor: colors.background }]}>
          <TextInput
            ref={inputRef}
            value={draft}
            onChangeText={setDraft}
            placeholder="Write a message..."
            placeholderTextColor={colors.placeholder}
            style={[styles.input, { color: colors.text }]}
            autoCorrect
            autoCapitalize="sentences"
            blurOnSubmit={false}
            editable={!sending}
            onSubmitEditing={() => {
              if (Platform.OS !== "web") sendMessage();
            }}
            onKeyPress={(e) => {
              if (Platform.OS === "web") {
                const k = e.nativeEvent.key;
                // Cast to any for web-specific shiftKey property
                if (k === "Enter" && !(e.nativeEvent as any).shiftKey) {
                  (e as any).preventDefault?.();
                  sendMessage();
                }
              }
            }}
            multiline={Platform.OS === "web"}
          />
        </View>
        <Button
          style={styles.sendButton}
          icon={sending ? "loading" : "send"}
          onPress={() => {
            sendMessage();
            Keyboard.dismiss();
          }}
          enabled={draft.trim().length > 0 && !sending}
        />
      </View>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  loadingText: {
    marginTop: 12,
    opacity: 0.7,
  },
  errorText: {
    textAlign: "center",
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  headerWithAvatar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  callButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: StyleSheet.hairlineWidth,
    backgroundColor: "transparent",
    paddingHorizontal: 0,
  },
  headerTextContainer: {
    flex: 1,
  },
  headerSubtitle: {
    fontSize: 13,
    marginTop: 2,
  },
  list: {
    flex: 1,
    // width: 780,
    // alignSelf: "center",
    // minWidth: 480,
    // marginHorizontal: "auto",
  },
  listContent: {
    padding: 16,
    gap: 12,
  },
  callEvent: {
    alignItems: "center",
    alignSelf: "center",
    gap: 4,
    paddingVertical: 4,
  },
  callEventPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  callEventText: {
    fontSize: 12,
    fontWeight: "500",
  },
  callEventTime: {
    fontSize: 10,
  },
  messageContainer: {
    alignItems: "flex-start",
    maxWidth: "80%",
  },
  messageContainerOutbound: {
    alignSelf: "flex-end",
    alignItems: "flex-end",
  },
  messageBubble: {
    padding: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 16,
  },
  messageText: {
    fontSize: 15,
  },
  timestamp: {
    fontSize: 11,
    marginTop: 4,
  },
  inputContainer: {
    flexDirection: "row",
    borderTopWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    gap: 12,
    padding: 12,
  },
  inputWrapper: {
    flex: 1,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: Platform.OS === "web" ? 6 : 0,
    justifyContent: "center",
  },
  input: {
    minHeight: 36,
    fontSize: 16,
    paddingVertical: 6,
    paddingHorizontal: 4,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  sendButton: {
    height: 36,
    width: 36,
    borderRadius: 999,
    transform: [{ rotate: "-90deg" }],
  },
});
